import axios, { AxiosError } from "axios";
import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { log } from "console";
import Loader from "../sub-components/Laoder";

interface PasswordChangeFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const Settings: React.FC = () => {
  const { user, setUser } = useUser();
  const submitPopupRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string>("");
  const deactivatePopupRef = useRef<HTMLDivElement>(null);
  const [otp, setOtp] = useState(["", "", "", ""]); // State for OTP inputs
  const [isSubmitPopupVisible, setSubmitPopupVisible] = useState(false);
  const [isDeactivatePopupVisible, setDeactivatePopupVisible] = useState(false);

  const showSubmitPopup = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setSubmitPopupVisible(true);
  };

  const showDeactivatePopup = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setDeactivatePopupVisible(true);
  };

  const closePopup = () => {
    setSubmitPopupVisible(false);
    setDeactivatePopupVisible(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        submitPopupRef.current &&
        !submitPopupRef.current.contains(event.target as Node)
      ) {
        closePopup();
      }
      if (
        deactivatePopupRef.current &&
        !deactivatePopupRef.current.contains(event.target as Node)
      ) {
        closePopup();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const userData = JSON.parse(sessionStorage.getItem("user") || "{}");

    if (userData?.accessToken) {
      console.log(userData?.accessToken);
      setToken(userData?.accessToken);
    } else {
      console.error("Access token not found");
    }
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    // Allow only one digit (0-9)
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to the next input if current input is filled
      if (value && index < otp.length - 1) {
        const nextInput = document.querySelector(
          `.otp__digit:nth-of-type(${index + 2})`
        ) as HTMLInputElement;
        nextInput?.focus();
      }
    }
  };

  const mergedOtp = otp.join(""); // Merged OTP value

  const formik = useFormik<PasswordChangeFormValues>({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validate: (values) => {
      const errors: Partial<PasswordChangeFormValues> = {};
      if (!values.currentPassword) {
        errors.currentPassword = "Current password is required.";
      }
      if (!values.newPassword) {
        errors.newPassword = "New password is required.";
      } else if (values.newPassword.length < 8) {
        errors.newPassword = "Password must be at least 8 characters long.";
      }
      if (values.confirmPassword !== values.newPassword) {
        errors.confirmPassword = "Passwords do not match.";
      }
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/change-password`,
          {
            email: user?.email,
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          toast.success(response.data.message);
          resetForm();
        } else if (response.status === 400) {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (error instanceof AxiosError && error.response) {
          if (error.response.status === 400) {
            toast.error(error.response.data.message); // Corrected: access the message property
          } else {
            toast.error("An unexpected error occurred. Please try again.");
          }
        } else {
          toast.error("Failed to change password. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <>
      <div className="home px-8 min-h-screen" id="background">
        <div className="flex flex-col md:flex-row pt-4 pb-4">
          <h3 className="text-white font-semibold p-2">Settings</h3>
        </div>

        <div className="settings mb-4 rounded-[16px] p-5">
          <div className="flex flex-col md:flex-row justify-between pt-4 pb-4">
            <div>
              <p className="text-white text-base font-bold p-2 pb-0">
                Password
              </p>
              <p className="text-white text-sm font-normal pl-2">
                If you wish to change your password, you can do so here.
              </p>
            </div>
            <div>
              <a
                href="#"
                className="text-white font-bold underline p-2"
                onClick={showDeactivatePopup}
              >
                {/* Deactivate Account */}
              </a>
            </div>
          </div>
          <div className="new_pass flex">
            <form className="max-w-2xl p-4" onSubmit={formik.handleSubmit}>
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-6">
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    className="appearance-none bg-transparent focus:outline-none w-full py-2 px-3 leading-tight focus:shadow-outline light-white"
                    placeholder="Enter your current password"
                    value={formik.values.currentPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {formik.touched.currentPassword &&
                    formik.errors.currentPassword && (
                      <div className="text-red-500 text-sm">
                        {formik.errors.currentPassword}
                      </div>
                    )}
                </div>
                <div className="col-span-6 md:col-span-3">
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    className="appearance-none bg-transparent focus:outline-none w-full py-2 px-3 leading-tight focus:shadow-outline light-white"
                    placeholder="Enter new password"
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {formik.touched.newPassword && formik.errors.newPassword && (
                    <div className="text-red-500 text-sm">
                      {formik.errors.newPassword}
                    </div>
                  )}
                </div>
                <div className="col-span-6 md:col-span-3">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    className="appearance-none bg-transparent focus:outline-none w-full py-2 px-3 leading-tight focus:shadow-outline light-white"
                    placeholder="Confirm password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {formik.touched.confirmPassword &&
                    formik.errors.confirmPassword && (
                      <div className="text-red-500 text-sm">
                        {formik.errors.confirmPassword}
                      </div>
                    )}
                </div>
              </div>
              <div className="mt-[34px]">
                <button
                  type="submit"
                  className="bg-blue-500 text-white text-base font-bold px-9 py-2 rounded-lg hover:bg-blue-600 transition duration-300 w-full md:w-auto"
                >
                  {isLoading ? (
                    <Loader isLoading={isLoading} />
                  ) : (
                    "Change Password"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Deactivate Account Popup */}
      {isDeactivatePopupVisible && (
        <div className="box fixed inset-0 flex items-center justify-center">
          <div
            ref={deactivatePopupRef}
            className="bg-white rounded-[20px] p-5 text-center"
            style={{
              backgroundColor: "#1E3359",
              color: "#fff",
              padding: "4rem",
            }}
          >
            <p className="msg text-xl">
              Are you sure you want to deactivate your account?
            </p>
            <div className="flex flex-row items-center justify-center space-x-3">
              <button
                className="mobile-button text-white border font-bold py-2 px-4 rounded-[10px] focus:outline-none w-[100px] mt-[20px] mb-[20px]"
                onClick={() => {
                  // Handle account deactivation logic here
                  closePopup();
                }}
              >
                Yes
              </button>
              <button
                className="mobile-button bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-[10px] focus:outline-none w-[100px] mt-[20px] mb-[20px]"
                onClick={closePopup}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Submission Popup */}
      {isSubmitPopupVisible && (
        <div className="box fixed inset-0 flex items-center justify-center">
          <div
            ref={submitPopupRef}
            className="otp bg-white rounded-[20px] p-5 text-center"
            style={{
              backgroundColor: "#1E3359",
              color: "#fff",
              padding: "4rem",
            }}
          >
            <div className="inline-block text-center">
              <h3 className="msg_head text-xl font-semibold">
                Verification Code
              </h3>
              <hr className="border-t-1 border-white my-2 w-[calc(100%-36px)] mx-auto" />
            </div>
            <p className="msg text-[14px] pb-2">
              We have sent a verification code to your email address.
            </p>

            <div className="otp-input-fields flex-col items-start">
              <h3 className="otp text-[16px] font-semibold pb-2">OTP Code</h3>
              <div className="flex justify-between space-x-2 w-full">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type={"text"}
                    maxLength={1} // Restricts the input to 1 character
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    className="otp__digit w-full text-center border border-gray-300 rounded"
                    required
                  />
                ))}
              </div>
            </div>
            <div className="flex flex-col items-center justify-between">
              <button
                className="mobile-button bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-[24px] focus:outline-none w-[330px] h-[40px] mt-[20px] mb-[20px]"
                onClick={() => {
                  console.log("Merged OTP:", mergedOtp); // Handle submission logic here
                  closePopup();
                }}
              >
                Submit
              </button>

              <p className="text-center font-normal text-white text-sm">
                OTP not received?&nbsp;
                <Link to="/settings" className="font-bold cursor-pointer">
                  Send again
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Settings;
